import React from 'react'
import { withRouter, RouteComponentProps } from 'react-router'

import { NavEventArgs, NavEvent } from 'common/events/nav-event-types'
import { SubNavigation } from 'component-library/navigation/TheWestMainNavigation/components/sub_navigation/subNavigation'
import { Section } from 'component-library/navigation/TheWestMainNavigation/components/sub_navigation/components/subNavigationSection'
import { MainNavigationItem } from 'component-library/navigation/TheWestMainNavigation/components/mainNavigationItem'

import {
    StyledNav,
    StyledNavWrapper,
    StyledMainNav,
    StyledSupportingNav,
    StyledSubscribeLink,
    StyledSubnavTrigger,
    StyledSubnavTriggerIcon,
    StyledSubNavTriggerText,
} from 'component-library/navigation/TheWestMainNavigation/TheWestMainNavigation.styled'
import {
    OnClickOutsideProps,
    wrapOnClickOutside,
} from 'component-library/__helpers/on-click-outside'
import { isCurrent } from 'common/helpers/link-utils'
import { TheWestNavigation } from 'component-library/routing/route-nav-types'
import { AuthenticationProvider } from 'component-library/user-registration/authentication/AuthenticationProvider'
import { createSubscribeLinkClickEvent } from 'component-library/user-registration/subscribe/createSubscribeClickLinkHandler'
import { SubscribeEvent } from 'common/events/subscribeEventTypes'
import InHouseLinks, { InHouseLink } from './components/in_house_links/in_house_links'
import { TheWestSection } from 'component-library/__styling/settings/sections/thewest'
import { DataLayerEventName } from 'common/helpers/DataLayerEventName'

export interface Props {
    includeSubNav?: boolean
    subNavOpen?: boolean
    includeHome?: boolean
    inHouseLinks?: InHouseLink[]
    navLinks: TheWestNavigation
    section: TheWestSection
    isFooterNav?: boolean
    onEvent: (event: NavEvent | SubscribeEvent) => void
}

export type InternalProps = Props & OnClickOutsideProps & RouteComponentProps<any>

export interface State {
    subNavOpen: boolean
}

class InternalMainNavigation extends React.Component<InternalProps, State> {
    mounted = false

    constructor(props: InternalProps) {
        super(props)
        props.setClickOutsiteTrigger(this.handleClickOutside)
        this.state = {
            subNavOpen: props.subNavOpen || false,
        }
    }

    handleClickOutside = () => {
        this.closeSubNav()
    }

    handleClick = (args: NavEventArgs) => {
        const isFooterNav = this.props.isFooterNav

        this.props.onEvent({
            type: DataLayerEventName.navClicked,
            originator: 'MainNavigation',
            payload: {
                navName: 'Watch.TheWest',
                navLocation: isFooterNav ? 'Footer' : 'Header',
                navPos: args.navPos,
                navText: args.navText,
                navLink: args.navLink,
                navPath: args.navParent ? `${args.navParent} > ${args.navText}` : `${args.navText}`,
            },
        })

        this.closeSubNav()
    }

    openSubNav = () => {
        if (this.state.subNavOpen === false) {
            this.setState({ subNavOpen: true })
            this.props.onEvent({
                type: DataLayerEventName.navSubNavOpened,
                originator: 'MainNavigation',
                payload: {},
            })
        }
    }

    closeSubNav = () => {
        if (this.state.subNavOpen === true) {
            this.setState({ subNavOpen: false })
            this.props.onEvent({
                type: DataLayerEventName.navSubNavClosed,
                originator: 'MainNavigation',
                payload: {},
            })
        }
    }

    toggleSubNav = () => {
        if (this.state.subNavOpen === true) {
            this.closeSubNav()
        } else {
            this.openSubNav()
        }
    }

    componentWillMount() {
        this.mounted = true
    }

    componentWillUnmount() {
        this.mounted = false
    }

    render() {
        const {
            includeHome,
            inHouseLinks,
            navLinks,
            includeSubNav,
            location,
            clickOutsideRef,
            section,
            onEvent,
            isFooterNav,
        } = this.props

        const navItems = navLinks[section]

        const subNavSections = navItems.map<Section>(val => ({
            sectionName: val.name,
            sectionLink: val.link,
            subNavLinks: val.subNavLinks ? val.subNavLinks : [],
        }))

        const isRegionalSite = section !== 'default'

        return (
            <StyledNav ref={clickOutsideRef}>
                <StyledNavWrapper>
                    <StyledMainNav>
                        {includeHome && (
                            <MainNavigationItem
                                name="Home"
                                link="/"
                                isCurrent={isCurrent(location, '/', { exact: true })}
                                onClick={this.closeSubNav}
                            />
                        )}
                        {navItems &&
                            navItems.map(({ name, link, hideFromMainNav, newWindow }, index) => {
                                if (!hideFromMainNav) {
                                    return (
                                        <MainNavigationItem
                                            key={name}
                                            name={name}
                                            link={link}
                                            newWindow={newWindow}
                                            isCurrent={isCurrent(location, link)}
                                            onClick={() =>
                                                this.handleClick({
                                                    navPos: index,
                                                    navText: name,
                                                    navLink: link,
                                                })
                                            }
                                        />
                                    )
                                }
                                return <React.Fragment key={name} />
                            })}
                        {inHouseLinks && (
                            <InHouseLinks isRegionalSite={isRegionalSite} items={inHouseLinks} />
                        )}
                    </StyledMainNav>
                    <StyledSupportingNav includeSubNav={includeSubNav}>
                        <AuthenticationProvider onEvent={onEvent}>
                            {({ onSubscribeClick }) => (
                                <StyledSubscribeLink
                                    onClick={() => {
                                        createSubscribeLinkClickEvent(
                                            isFooterNav ? 'footer-nav' : 'header-nav',
                                            onEvent
                                        )()
                                        onSubscribeClick()
                                    }}
                                >
                                    Subscribe
                                </StyledSubscribeLink>
                            )}
                        </AuthenticationProvider>

                        {includeSubNav && (
                            <StyledSubnavTrigger onClick={this.toggleSubNav}>
                                <StyledSubnavTriggerIcon subNavOpen={this.state.subNavOpen} />
                                <StyledSubNavTriggerText>Menu</StyledSubNavTriggerText>
                            </StyledSubnavTrigger>
                        )}
                    </StyledSupportingNav>
                    {includeSubNav && subNavSections && this.state.subNavOpen && (
                        <SubNavigation onClick={this.handleClick} subNavSections={subNavSections} />
                    )}
                </StyledNavWrapper>
            </StyledNav>
        )
    }
}

export const TheWestMainNavigation = withRouter(
    wrapOnClickOutside<Props & RouteComponentProps<any>>(InternalMainNavigation)
)
